import VueWrapper from '@/components/core/Vue/vue.wrapper';
import ClientFooterComponent from '@/components/vuetify/client-footer/client-footer.component';
import {PricingPlan} from '@/globals';
import {PaymentApiService} from '@/sdk/api-services/payment.api';
import {UserSetupService} from '@/views/shared/services';
import {loadPaymentWidget, PaymentWidgetInstance} from '@tosspayments/payment-widget-sdk';
import {Component} from 'vue-property-decorator';

@Component({
    components: {
        ClientFooterComponent,
    },
})
export default class CheckoutComponent extends VueWrapper {
    private readonly widgetClientKey = process.env.VUE_APP_TOSS_WIDGET_CLIENT_KEY ?? 'live_gck_ALnQvDd2VJGaZjAAoNqwrMj7X41m';
    private customerKey: string = '';

    public isLoading: boolean = true;
    public isWidgetReady: boolean = false;
    private paymentWidget: PaymentWidgetInstance | null = null;

    public selectedPlan: PricingPlan | null = null;
    public hasCircuitModule: boolean = false;
    public extraUsersCount: number = 0;

    public orderId: string = '';
    public orderName: string = '';
    public totalAmountKrw: number = 0;

    private readonly plans: PricingPlan[] = [
        {id: 'st200', name: 'St 200', priceUsd: 1200, includedUsers: 5},
        {id: 'st500', name: 'St 500', priceUsd: 2400, includedUsers: 5},
        {id: 'st1000', name: 'St 1000', priceUsd: 4000, includedUsers: 5},
    ];

    public readonly exchangeRate: number = 1350;

    public userSetupSrv: UserSetupService = new UserSetupService();
    public paymentSrv: PaymentApiService = new PaymentApiService();

    public created() {
        this.customerKey = this.UserSessionSrv.accountId!;
        this.parseUrlParams();
    }

    public async mounted() {
        if (!this.selectedPlan) {
            this.isLoading = false;
            this.$router.push({name: 'SubscriptionPlan'});
            return;
        }

        try {
            await this.initializeOrderOnBackend();

            this.isLoading = false;

            await this.$nextTick();

            await this.initializePaymentWidget();
        } catch (error) {
            console.error('Initialization failed', error);
            alert('Failed to initialize order. Please try again.');
            this.$router.push({name: 'SubscriptionPlan'});
            this.isLoading = false;
        }
    }

    private parseUrlParams() {
        const query = this.$route.query;
        const planId = query.planId as string;
        this.hasCircuitModule = query.includeCircuit === 'true';
        this.extraUsersCount = parseInt(query.extraUsers as string, 10) || 0;
        this.selectedPlan = this.plans.find(p => p.id === planId) || null;
    }

    private async initializeOrderOnBackend() {
        if (!this.selectedPlan) return;

        const request = {
            userId: this.UserSessionSrv.userId!,
            planId: this.selectedPlan.id,
            includeCircuit: this.hasCircuitModule,
            extraUsers: this.extraUsersCount,
        };

        const paymentRecord = await this.paymentSrv.initPayment(request);

        this.orderId = paymentRecord.orderId;
        this.totalAmountKrw = paymentRecord.amount;
        this.orderName = paymentRecord.orderName;
    }

    private async initializePaymentWidget() {
        try {
            this.paymentWidget = await loadPaymentWidget(this.widgetClientKey, this.customerKey);

            await this.paymentWidget.renderPaymentMethods('#payment-widget', {value: this.totalAmountKrw}, {variantKey: 'DEFAULT'});

            await this.paymentWidget.renderAgreement('#payment-agreement');

            this.isWidgetReady = true;
        } catch (error) {
            console.error('Failed to initialize Toss Payment Widget:', error);
            alert(this.$t('PaymentPage.Checkout.alertWidgetLoadFailed'));
        }
    }

    public async requestPayment() {
        if (!this.paymentWidget || !this.isWidgetReady) {
            alert(this.$t('PaymentPage.Checkout.alertWidgetNotReady'));
            return;
        }

        const currentQuery = new URLSearchParams(this.$route.query as Record<string, string>);

        const successUrl = `${window.location.origin}/payment/success?${currentQuery.toString()}`;
        const failUrl = `${window.location.origin}/payment/fail?${currentQuery.toString()}`;

        console.log('Requesting payment with:', {
            orderId: this.orderId,
            orderName: this.orderName,
            successUrl: successUrl,
            failUrl: failUrl,
        });

        try {
            await this.paymentWidget.requestPayment({
                orderId: this.orderId,
                orderName: this.orderName,
                successUrl: successUrl,
                failUrl: failUrl,
                customerEmail: this.UserSessionSrv.Session?.email || undefined,
                customerName: this.UserSessionSrv.Session?.FirstName + ' ' + this.UserSessionSrv.Session?.LastName || undefined,
            });
        } catch (error) {
            console.error('Payment request failed:', error);
            alert(this.$t('PaymentPage.Checkout.alertPaymentFailed', {error: error}));
        }
    }

    get planPriceKrw(): number {
        if (!this.selectedPlan) return 0;
        return this.selectedPlan.priceUsd * this.exchangeRate;
    }

    get circuitPriceKrw(): number {
        return this.hasCircuitModule ? 500 * this.exchangeRate : 0;
    }

    get extraUsersPriceKrw(): number {
        return this.extraUsersCount * 30000;
    }

    public formatPrice(value: number, currency: 'KRW' = 'KRW'): string {
        return new Intl.NumberFormat('ko-KR', {
            style: 'currency',
            currency: currency,
        }).format(value);
    }
}
